import '@material/web/icon/icon.js'
import './data-list-gutter'
import './data-list-field'
import '../record-view'

import { css, html, LitElement, PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { ZERO_CONFIG, ZERO_DATA, ZERO_RECORD } from '../configure/zero-config'
import { RecordViewHandler } from '../record-view/record-view-handler'
import { GristConfig, GristData, GristRecord } from '../types'
import { recordPartialClickHandler } from './event-handlers/record-partial-click-handler'
import { recordPartialDblClickHandler } from './event-handlers/record-partial-dblclick-handler'

// import { recordPartialLongPressHandler } from './event-handlers/record-partial-long-press-handler'

// TODO 로케일 설정에 따라서 포맷이 바뀌도록 해야한다.
const OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hour12: false
  // timeZone: 'America/Los_Angeles'
}

const formatter = new Intl.DateTimeFormat(navigator.language, OPTIONS)

@customElement('ox-record-partial')
export class RecordPartial extends LitElement {
  static styles = [
    css`
      :host {
        display: flex;
        flex-direction: row;
        align-items: center;
        border-bottom: var(--data-list-item-border-bottom);
        position: relative;
        min-height: 42px;
        padding-left: var(--spacing-medium);
      }

      :host([dirty])::before {
        content: '';
        position: absolute;
        left: var(--spacing-none);
        top: var(--spacing-none);

        width: var(--spacing-none);
        height: var(--spacing-none);
        border-top: var(--grid-record-dirty-border-top);
        border-right: var(--grid-record-dirty-border-left);
      }

      :host[emphasized-row] {
        background-color: var(--grid-record-emphasized-background-color);
        color: var(--grid-record-emphasized-color);
      }

      :host [dirty] {
        position: absolute;
        margin: var(--spacing-none);
        height: 20px;
        font: var(--grid-record-dirty-icon-font);
        text-indent: 1px;
        left: var(--spacing-none);
        top: var(--spacing-none);
        color: var(--grid-record-dirty-color, var(--md-sys-color-error));
      }

      [content] {
        flex: auto;
        display: block;
        zoom: 1;
      }

      div[tail] {
        margin: var(--spacing-none);
        width: 100px;
        aspect-ratio: 1 / 1;

        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }

      div[tail] > ox-list-field {
        flex: 1;
      }

      ox-list-field {
        font: var(--data-list-item-etc-font);
        color: var(--data-list-item-etc-color, var(--md-sys-color-on-surface));
      }

      ox-list-field[name] {
        font: var(--data-list-item-name-font);
        color: var(--data-list-item-name-color, var(--md-sys-color-secondary));
      }

      ox-list-field[desc] {
        font: var(--data-list-item-disc-font);
        color: var(--data-list-item-disc-color, var(--md-sys-color-on-tertiary-container));
      }
    `
  ]

  @property({ type: Object }) config: GristConfig = ZERO_CONFIG
  @property({ type: Object }) data: GristData = ZERO_DATA
  @property({ type: Object }) record: GristRecord = ZERO_RECORD
  @property({ type: Number }) rowIndex: number = -1
  /*
   * row-selector를 사용자가 변경할 때, record-partial의 update를 유도하기 위해 selected-row attribute를 property에 추가함.
   * (이를 해주지 않으면, 리스트 refresh 경우에 selected-row checkbox가 클리어되지 않는 현상이 발생함.)
   */
  @property({ type: Boolean, attribute: 'selected-row' }) selectedRow: boolean = false
  @property({ attribute: false }) emphasized: any = false

  private _recordView: any

  attributeChangedCallback(name: string, oldval: any, newval: any) {
    super.attributeChangedCallback(name, oldval, newval)
  }

  firstUpdated() {
    /*
      long-press
      TODO. performance를 확인한 후에 활성화하자.
    */
    // longpressable(this.renderRoot.querySelector('[content]'))

    // this.renderRoot.addEventListener('long-press', recordPartialLongPressHandler.bind(this))
    this.renderRoot.addEventListener('click', recordPartialClickHandler.bind(this) as EventListener)
    this.renderRoot.addEventListener('dblclick', recordPartialDblClickHandler.bind(this) as EventListener)

    this.addEventListener('show-record-view', () => this.popupRecordView())
  }

  updated(changes: PropertyValues<this>) {
    if (changes.has('record') && this._recordView) {
      this._recordView.record = this.record
    }

    const emphasized = this.emphasized

    if (!!emphasized) {
      this.setAttribute('emphasized-row', '')

      if (Symbol.iterator in Object(emphasized)) {
        const [backgroundColor, foregroundColor] = emphasized as string[]
        backgroundColor && this.style.setProperty('--grid-record-emphasized-background-color', backgroundColor)
        foregroundColor && this.style.setProperty('--grid-record-emphasized-color', foregroundColor)
      }
    } else {
      this.removeAttribute('emphasized-row')
    }
  }

  render() {
    var record = this.record
    var rowIndex = this.rowIndex
    var { columns, list } = this.config
    var { thumbnail, fields, details } = list || {}

    var displayColumns =
      [...fields, ...details].map(field => columns.find(column => column.name == field)).filter(column => column) || []
    var gutters = (columns || []).filter(column => column.type == 'gutter' && column.forList)

    const thumbnailColumn = thumbnail ? columns.find(column => column.name == thumbnail) : undefined

    if (this.hasAttribute('dirty')) {
      var dirtyIcon

      switch (this.record['__dirty__']) {
        case 'M':
          dirtyIcon = 'done'
          break
        case '+':
          dirtyIcon = 'add'
          break
        case '-':
          dirtyIcon = 'remove'
          break
      }
    }

    return html`
      ${dirtyIcon ? html` <md-icon dirty>${dirtyIcon}</md-icon> ` : html``}
      ${gutters.map(
        gutter => html`
          <ox-list-gutter
            class=${gutter.name === 'detail' ? 'expand' : ''}
            .rowIndex=${rowIndex}
            .column=${gutter}
            .record=${record}
            .value=${record[gutter.name]}
          ></ox-list-gutter>
        `
      )}

      <div content>
        ${displayColumns.map(
          (column, idx) => html`
            <ox-list-field
              .rowIndex=${rowIndex}
              .column=${column}
              .record=${record}
              .value=${record[column?.name || '']}
              ?name=${idx == 0}
              ?desc=${idx == 1}
            ></ox-list-field>
          `
        )}
      </div>
      ${thumbnail
        ? html`<div tail>
            <ox-list-field
              thumbnail
              .rowIndex=${rowIndex}
              .column=${thumbnailColumn}
              .record=${record}
              .value=${record[thumbnailColumn?.name || '']}
            ></ox-list-field>
          </div>`
        : html``}
    `
  }

  popupRecordView() {
    var titleField = this.config.list.fields[0] || 'name'
    var title = this.record[titleField]

    /* field가 오브젝트형인 경우에는 렌더러를 타이틀로 사용한다. */
    if (typeof title == 'object') {
      var column = this.config.columns.find(column => column.name == titleField)
      title = column?.record.renderer(title, column, this.record, this.rowIndex, this /* cautious */)
    }

    this._recordView = RecordViewHandler(
      this.config.columns,
      this.record,
      this.rowIndex,
      this,
      {
        title
      },
      () => {
        delete this._recordView
      }
    )
  }
}
